import React, { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import { RegulationContext } from "../../../App";
import ReportingPeriod from "../../../components/Regulation/ReportingPeriod";
import { LS_AUTHORITY_KEY, LS_DV_FILTER_KEY } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import Utils from "../../../core/utilities/Utils";
import "./ModernKpiFilter.scss";

const ModernKpiFilter = ({ t, onApplyFilter, onFilterHide, goalId, onLoad }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const year = new Date().getFullYear();
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsData.profile.company.id;

  const [filter, setFilter] = useState({
    reportingPeriod: `${year}-01-01`,
    goalId: goalId ?? '',
    kpiId: '',
    kpiCategory: '',
    periodFrom: `${year}-01-01`,
    periodTo: `${year}-12-31`,
    periodType: 'YEARLY',
    period: null
  });

  const [goals, setGoals] = useState([]);
  const [kpis, setKPIs] = useState([]);
  const [kpiCategories, setKPICategories] = useState([]);
  const [displayFilter, setDisplayFilter] = useState(true);

  // Always render the filter content for auto-loading, but control visibility through CSS

  useEffect(() => {
    console.log('useEffect triggered, goalId:', goalId);
    fetchKPICategories();
    fetchGoals(true); // Pass firstTime=true to auto-load graphs
    if (goalId) {
      console.log('Fetching KPIs for initial goalId:', goalId);
      fetchKPIs(goalId);
    }
  }, [goalId]);

  const fetchKPICategories = () => {
    KPIService.listKPICategory(companyId).then(res => {
      if (res.status !== 200) return;
      setKPICategories(res.data.data ?? []);
    }).catch(err => {
      // Handle error
    });
  };

  const fetchGoals = (firstTime = false) => {
    console.log('Fetching goals, firstTime:', firstTime, 'category:', filter.kpiCategory);
    // If no category is selected, fetch all goals (empty string for category)
    const category = filter.kpiCategory || '';
    KPIService.listGoals(companyId, category).then(res => {
      console.log('Goals response:', res);
      if (res.status !== 200) return;
      const goalsData = res.data.data ?? [];
      console.log('Setting goals:', goalsData);
      setGoals(goalsData);
      if (firstTime && goalsData.length > 0) {
        const selectedGoalId = goalId ?? goalsData[0].id;
        const f = { ...filter, goalId: selectedGoalId };
        console.log('Setting filter with goalId:', f.goalId);
        setFilter(f);
        // Fetch KPIs for the selected goal
        fetchKPIs(selectedGoalId);
        // Auto-load graphs with first goal when component mounts
        onLoad({ type: 'GOALS', filter: f });
      }
    }).catch(err => {
      console.error('Error fetching goals:', err);
      // Handle error
    });
  };

  const fetchKPIs = (goalId) => {
    console.log('Fetching KPIs for goalId:', goalId);
    KPIService.getKPIs(goalId).then(res => {
      console.log('KPIs response:', res);
      if (res.status !== 200) return;
      const kpiData = res.data.data ?? [];
      console.log('Setting KPIs:', kpiData);
      setKPIs(kpiData);
    }).catch(err => {
      console.error('Error fetching KPIs:', err);
      // Handle error
    });
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'SOCIAL': return t("Social.label");
      case 'ENVIRONMENTAL': return t("Environment.label");
      case 'GOVERNANCE': return t("Governance.label");
      default:
        break;
    }
  };

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    const updatedFilter = { ...filter, kpiCategory: selectedCategory, goalId: '', kpiId: '' };
    setFilter(updatedFilter);
    setKPIs([]);
    // Always fetch goals when category changes (including when no category is selected)
    fetchGoals(false);
  };

  const handleGoalChange = (e) => {
    const selectedGoalId = e.target.value;
    console.log('Goal selected:', selectedGoalId);
    const updatedFilter = { ...filter, goalId: selectedGoalId, kpiId: '' };
    setFilter(updatedFilter);
    if (selectedGoalId) {
      fetchKPIs(selectedGoalId);
    } else {
      // Clear KPIs when no goal is selected
      setKPIs([]);
    }
  };

  const handleKpiChange = (e) => {
    const selectedKpiId = e.target.value;
    setFilter(prev => ({ ...prev, kpiId: selectedKpiId }));
  };

  const handleReportingPeriodChange = (period) => {
    setFilter(prev => ({
      ...prev,
      reportingPeriod: period,
      periodFrom: `${new Date(period).getFullYear()}-01-01`,
      periodTo: `${new Date(period).getFullYear()}-12-31`
    }));
  };

  const applyFilter = () => {
    onApplyFilter(filter);
    onFilterHide(false);
  };

  const resetFilter = () => {
    const resetFilter = {
      reportingPeriod: `${year}-01-01`,
      goalId: goalId ?? '',
      kpiId: '',
      kpiCategory: '',
      periodFrom: `${year}-01-01`,
      periodTo: `${year}-12-31`,
      periodType: 'YEARLY',
      period: null
    };
    setFilter(resetFilter);
    // Auto-load graphs with reset values
    onLoad({ type: 'RESET', filter: resetFilter });
    onApplyFilter(resetFilter);
  };

  const renderCategoryFilter = () => {
    return (
      <div className="col-lg-6 col-md-6 col-sm-12 p-2">
        <Form.Group>
          <Form.Label className="mb-1">{t("Category.label")}</Form.Label>
          <Form.Select
            aria-label={t("Category.label")}
            value={filter.kpiCategory}
            onChange={handleCategoryChange}
            className="form-select"
          >
            <option value="">{t("All.label")}</option>
            {kpiCategories?.map((cat) => (
              <option key={cat.id} value={cat.category}>
                {getCategoryName(cat.category.toUpperCase())?.toUpperCase()}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
    );
  };

  const renderGoalFilter = () => {
    return (
      <div className="col-lg-6 col-md-6 col-sm-12 p-2">
        <Form.Group>
          <Form.Label className="mb-1">{t("Goal.label")}</Form.Label>
          <Form.Select
            value={filter.goalId}
            onChange={handleGoalChange}
            className="form-select"
          >
            <option value="">{t("All.label")}</option>
            {goals.map((goal) => (
              <option key={goal.id} value={goal.id}>
                {goal.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
    );
  };

  const renderKpiFilter = () => {
    console.log('Rendering KPI filter, kpis:', kpis, 'filter.goalId:', filter.goalId);
    return (
      <div className="col-lg-6 col-md-6 col-sm-12 p-2">
        <Form.Group>
          <Form.Label className="mb-1">{t("KPI.label")}</Form.Label>
          <Form.Select
            value={filter.kpiId}
            onChange={handleKpiChange}
            className="form-select"
            disabled={!filter.goalId}
          >
            <option value="">{t("All.label")}</option>
            {kpis.map((kpi) => (
              <option key={kpi.id} value={kpi.id}>
                {kpi.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
    );
  };

  const renderYearFilter = () => {
    return (
      <div className="col-lg-6 col-md-6 col-sm-12 p-2">
        <Form.Group>
          <Form.Label className="mb-1">{t("Year")}</Form.Label>
          <ReportingPeriod
            defaultFrequency='ANNUAL'
            defaultPeriod={filter.reportingPeriod}
            valueChangeCallback={(period) => {
              setFilter({ ...filter, reportingPeriod: `${period.year}-01-01` });
            }}
          />
        </Form.Group>
      </div>
    );
  };

  return (
    <div className="modern-kpi-filter-container" data-testid="modern-kpi-filter">
      <div className="modern-filter-panel">
        <div className="modern-filter-header">
          <h5>{t("Filter.label")}</h5>
          <div className="modern-filter-close" onClick={() => onFilterHide(false)}>
            <BiX />
          </div>
        </div>
        <div className="modern-filter-body">
          <div className="row m-0">
            {renderCategoryFilter()}
            {renderGoalFilter()}
            {renderKpiFilter()}
            {renderYearFilter()}
          </div>
        </div>
        <div className="modern-filter-footer02 text-center">
          <Button
            variant="primary"
            onClick={applyFilter}
            className="btn px-4"
          >
            {t("Apply.label")}
          </Button>
          <Button
            variant="secondary"
            onClick={resetFilter}
            className="btn px-4"
          >
            {t("Reset.label")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(ModernKpiFilter); 